"use client"

import React from "react"
import { CircleAlert } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/shadcn/components/ui/alert"
import { Button } from "@/shadcn/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card"
import {
  Field,
  FieldError as ShadFieldError,
} from "@/shadcn/components/ui/field"
import { Input } from "@/shadcn/components/ui/input"
import { cn } from "@/lib/utils"
import ErrorModule from "app/mixins/ErrorModule.jsx"
import FieldError from "app/components/common/FieldError.jsx"

import {
  CARD,
  CARD_DESCRIPTION,
  CARD_HEADER,
  CARD_TITLE,
  SM_BUTTON,
} from "./styles"

export default function EmailCard({
  email,
  isEditing,
  onEdit,
  onChange,
  onSave,
  onCancel,
  onVerify,
  app,
  emailActive,
  bounceCount,
  isFieldInvalid,
}: {
  email: string
  isEditing: Boolean
  onEdit: () => void
  onChange: () => void
  onSave: () => void
  onCancel: () => void
  onVerify: () => void
  app: any
  emailActive: number
  bounceCount: number
  isFieldInvalid: (section: string) => Boolean
}) {
  let verificationCopy =
    "Verify your email so we can send you service alerts when one of your feeds breaks."
  if (bounceCount > 0) {
    verificationCopy =
      "Your emails have been bouncing. Update your email address or click here to re-verify"
  }

  const isEmailInvalid = isFieldInvalid("email")

  return (
    <Card className={CARD}>
      <CardHeader className={CARD_HEADER}>
        <CardTitle className={CARD_TITLE}>Email</CardTitle>
        <CardDescription className={CARD_DESCRIPTION}>
          Used for sign-in and service alerts.
        </CardDescription>
      </CardHeader>
      <CardContent
        className={cn(
          "tw:flex tw:flex-col tw:px-4 tw:pb-4",
          !emailActive || bounceCount > 0 ? "tw:gap-3" : "tw:gap-0"
        )}
      >
        <div
          className={
            isEditing
              ? "tw:flex tw:flex-col tw:gap-2"
              : "tw:flex tw:flex-col tw:gap-2 tw:md:flex-row tw:md:items-start"
          }
        >
          <Field data-invalid={isEmailInvalid}>
            <Input
              key={isEditing ? "edit" : "view"}
              defaultValue={email}
              placeholder={isEditing ? "you@example.com" : undefined}
              readOnly={!isEditing}
              aria-label="Email address"
              className="tw:h-8 tw:flex-1 tw:rounded-lg tw:px-2.5 tw:py-1 tw:text-sm tw:font-normal tw:leading-5"
              onChange={onChange}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (isEditing && e.key === "Escape") onCancel()
              }}
              data-field="email"
            />
            <ErrorModule
              app={app}
              keys={['USER_UPDATE']}
              render={(state: any) => {
                return <FieldError field="email" errors={state.errors} element={ShadFieldError} />
              }}
            />
          </Field>
          {isEditing ? (
            <div className="tw:flex tw:justify-end tw:gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={SM_BUTTON}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className={SM_BUTTON}
                onClick={onSave}
              >
                Save changes
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(SM_BUTTON, "tw:self-start tw:md:self-auto")}
              onClick={onEdit}
            >
              Change email
            </Button>
          )}
        </div>

        {(!emailActive || bounceCount > 0) && (
          <Alert
            className={cn(
              "tw:gap-x-2 tw:gap-y-1 tw:rounded-lg tw:p-3",
              "tw:border-amber-200 tw:bg-amber-50 tw:text-amber-800",
              "tw:[&>svg]:size-[18px] tw:[&>svg]:text-amber-800"
            )}
          >
            <CircleAlert aria-hidden />
            <AlertTitle className="tw:text-sm tw:font-medium tw:leading-5 tw:text-amber-800">
              Email not verified
            </AlertTitle>
            <AlertDescription className="tw:flex tw:flex-col tw:gap-2 tw:text-sm tw:font-normal tw:leading-5 tw:text-amber-800">
              <p>{verificationCopy}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(SM_BUTTON, "tw:w-fit")}
                onClick={onVerify}
              >
                Resend email verification
              </Button>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
